import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, MapPin, Bed, Bath, SquareIcon, Star, CheckCircle2, Shield,
  TrendingUp, AlertTriangle, Play, Heart, Share2, Calendar,
  Lock, Eye, Unlock, Loader2, FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';
import api from '@/services/api';
import VerifiedInterestFlow from '@/components/VerifiedInterestFlow';
import { RashiiConfidenceMeter, PersonalizedScoreCard, AIAnalysisCard } from '@/components/RashiiIntelligence';
import AEDIcon from '@/components/icons/AEDIcon';
import { MarketIntelligenceReport, MarketIntelligenceLoader, MarketIntelligenceError } from '@/components/MarketIntelligenceReport';
import PropertyGallery from '@/components/PropertyGallery';

export const PropertyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  // Property states
  const [publicProperty, setPublicProperty] = useState(null);
  const [unlockedProperty, setUnlockedProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  
  // Market Intelligence states
  const [marketIntelligence, setMarketIntelligence] = useState(null);
  const [marketIntelLoading, setMarketIntelLoading] = useState(false);
  const [marketIntelError, setMarketIntelError] = useState(false);
  
  // Gating flow state
  const [showGatingFlow, setShowGatingFlow] = useState(false);
  
  // AI Analysis loading state
  const [aiLoading, setAiLoading] = useState(false);
  
  // Offer state
  const [offerAmount, setOfferAmount] = useState('');
  const [offerMessage, setOfferMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPropertyData();
  }, [id, isAuthenticated]);

  const fetchPropertyData = async () => {
    setLoading(true);
    try {
      // Always try to get the direct property first
      const propRes = await api.get(`/properties/${id}`);
      const propData = propRes.data;
      
      setPublicProperty({
        id: propData.id,
        title: propData.title,
        property_type: propData.property_type,
        listing_type: propData.listing_type,
        price: propData.price,
        city: propData.city,
        area: propData.location?.split(',')[0] || propData.city,
        bedrooms: propData.bedrooms,
        bathrooms: propData.bathrooms,
        area_sqft: propData.area_sqft,
        preview_images: propData.images?.slice(0, 3) || [],
        rashii_score_headline: propData.rashii_score?.overall_score || 70,
        estimated_yield_range: `${propData.rashii_score?.rental_yield?.min || 5}-${propData.rashii_score?.rental_yield?.max || 7}%`,
        is_verified: propData.verification_status === 'verified',
        is_locked: true
      });
      setOfferAmount(propData.price?.toString() || '');
      
      // Check if user has access (only for authenticated users)
      if (isAuthenticated) {
        try {
          const accessRes = await api.get(`/properties/${id}/check-access`);
          if (accessRes.data.has_access) {
            // User already has access, fetch full data with loading state
            setAiLoading(true);
            setMarketIntelLoading(true);
            try {
              const fullRes = await api.post(`/properties/${id}/unlock`);
              setUnlockedProperty(fullRes.data);
              setMarketIntelligence(fullRes.data.market_intelligence);
              setHasAccess(true);
            } catch (unlockError) {
              console.error('Failed to unlock property:', unlockError);
              setMarketIntelError(true);
            } finally {
              setAiLoading(false);
              setMarketIntelLoading(false);
            }
          }
        } catch (error) {
          // No access yet or error checking - that's fine
          setHasAccess(false);
        }
      }
    } catch (error) {
      console.error('Failed to fetch property:', error);
      setPublicProperty(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProperty = () => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=/property/${id}`);
      return;
    }
    setShowGatingFlow(true);
  };

  const handleUnlockComplete = (data) => {
    setUnlockedProperty(data);
    setMarketIntelligence(data.market_intelligence);
    setHasAccess(true);
    setShowGatingFlow(false);
  };

  const handleRetryMarketIntel = async () => {
    if (!id) return;
    setMarketIntelLoading(true);
    setMarketIntelError(false);
    try {
      const res = await api.get(`/properties/${id}/market-analysis`);
      setMarketIntelligence(res.data.market_intelligence);
    } catch (error) {
      console.error('Failed to fetch market intelligence:', error);
      setMarketIntelError(true);
    } finally {
      setMarketIntelLoading(false);
    }
  };

  const handleShortlist = async () => {
    if (!hasAccess) {
      handleSelectProperty();
      return;
    }
    try {
      await api.post('/interests', { property_id: id, stage: 'shortlisted' });
      alert('Property added to shortlist!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to shortlist');
    }
  };

  const handleMakeOffer = async () => {
    if (!hasAccess) {
      handleSelectProperty();
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/offers', {
        property_id: id,
        offer_amount: parseFloat(offerAmount),
        message: offerMessage
      });
      alert('Offer submitted successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to submit offer');
    }
    setSubmitting(false);
  };

  const formatPrice = (price, type) => {
    if (price >= 1000000) {
      return `AED ${(price / 1000000).toFixed(1)}M${type === 'rent' ? '/yr' : ''}`;
    }
    return `AED ${price?.toLocaleString()}${type === 'rent' ? '/yr' : ''}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary">Loading property...</div>
      </div>
    );
  }

  if (!publicProperty) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-muted-foreground">Property not found</p>
          <Button className="mt-4" onClick={() => navigate('/')}>Go Home</Button>
        </div>
      </div>
    );
  }

  // Use unlocked data if available, otherwise public data
  const property = hasAccess && unlockedProperty ? unlockedProperty.property : publicProperty;
  const baseScore = hasAccess ? unlockedProperty?.base_score : null;
  const personalizedScore = hasAccess ? unlockedProperty?.personalized_score : null;
  const confidenceMeter = hasAccess ? unlockedProperty?.confidence_meter : null;
  const aiAnalysis = hasAccess ? unlockedProperty?.ai_analysis : null;

  return (
    <div className="min-h-screen bg-background" data-testid="property-detail-page">
      <div className="noise-overlay" />
      
      {/* Gating Flow Modal */}
      {showGatingFlow && (
        <VerifiedInterestFlow 
          propertyId={id}
          onComplete={handleUnlockComplete}
          onClose={() => setShowGatingFlow(false)}
        />
      )}
      
      {/* Header */}
      <nav className="sticky top-0 z-40 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16">
            <Button variant="ghost" onClick={() => navigate(-1)} className="hover:text-primary">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div className="flex items-center gap-2">
              {hasAccess ? (
                <Badge className="bg-green-600/20 text-green-400 border-green-600/30">
                  <Unlock className="w-3 h-3 mr-1" /> Full Access
                </Badge>
              ) : (
                <Badge className="bg-gold-600/20 text-gold-400 border-gold-600/30">
                  <Lock className="w-3 h-3 mr-1" /> Limited View
                </Badge>
              )}
              <Button variant="outline" size="icon">
                <Share2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>
      
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-8 space-y-8">
            {/* Photo Gallery - Always visible, no gating */}
            <PropertyGallery 
              images={property.images || property.preview_images || []} 
              title={property.title}
            />
            
            {/* Badges Row */}
            <div className="flex flex-wrap gap-2">
              <Badge className="bg-primary text-primary-foreground uppercase text-xs">
                {property.listing_type === 'buy' ? 'For Sale' : 'For Rent'}
              </Badge>
              {(property.is_verified || property.verification_status === 'verified') && (
                <Badge className="bg-green-600 text-white uppercase text-xs">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Verified
                </Badge>
              )}
              {property.is_exclusive && (
                <Badge className="bg-purple-600/20 text-purple-400 border-purple-600/30 uppercase text-xs">
                  Exclusive
                </Badge>
              )}
              <Badge className="bg-black/80 backdrop-blur-sm">
                <Star className="w-3 h-3 text-primary fill-primary mr-1" />
                <span className="text-sm font-bold text-primary">
                  {publicProperty.rashii_score_headline || baseScore?.overall_score || 70}
                </span>
              </Badge>
            </div>
            
            {/* Title & Location */}
            <div>
              <h1 className="font-display text-3xl md:text-4xl tracking-tight mb-2">
                {property.title}
              </h1>
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="w-4 h-4" />
                <span>
                  {hasAccess && property.exact_location ? property.exact_location : property.area}, {property.city}
                </span>
                {!hasAccess && (
                  <Badge variant="outline" className="text-xs border-gold-600/30 text-gold-400">
                    <Lock className="w-3 h-3 mr-1" /> Exact location locked
                  </Badge>
                )}
              </div>
            </div>
            
            {/* Quick Stats */}
            <div className="grid grid-cols-4 gap-4">
              <Card className="glass-card border-white/10">
                <CardContent className="p-4 text-center">
                  <Bed className="w-5 h-5 mx-auto text-primary mb-2" />
                  <p className="text-lg font-medium">{property.bedrooms}</p>
                  <p className="text-xs text-muted-foreground">Bedrooms</p>
                </CardContent>
              </Card>
              <Card className="glass-card border-white/10">
                <CardContent className="p-4 text-center">
                  <Bath className="w-5 h-5 mx-auto text-primary mb-2" />
                  <p className="text-lg font-medium">{property.bathrooms}</p>
                  <p className="text-xs text-muted-foreground">Bathrooms</p>
                </CardContent>
              </Card>
              <Card className="glass-card border-white/10">
                <CardContent className="p-4 text-center">
                  <SquareIcon className="w-5 h-5 mx-auto text-primary mb-2" />
                  <p className="text-lg font-medium">{property.area_sqft?.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Sq Ft</p>
                </CardContent>
              </Card>
              <Card className="glass-card border-white/10">
                <CardContent className="p-4 text-center">
                  <TrendingUp className="w-5 h-5 mx-auto text-primary mb-2" />
                  <p className="text-lg font-medium">{publicProperty.estimated_yield_range || '5-7%'}</p>
                  <p className="text-xs text-muted-foreground">Est. Yield</p>
                </CardContent>
              </Card>
            </div>
            
            {/* Description - Only if unlocked */}
            {hasAccess && property.description && (
              <Card className="glass-card border-white/10">
                <CardHeader>
                  <CardTitle className="font-display text-xl">Description</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed">{property.description}</p>
                </CardContent>
              </Card>
            )}
            
            {/* Amenities - Only if unlocked */}
            {hasAccess && property.amenities?.length > 0 && (
              <Card className="glass-card border-white/10">
                <CardHeader>
                  <CardTitle className="font-display text-xl">Amenities</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {property.amenities.map((amenity, idx) => (
                      <Badge key={idx} variant="outline" className="border-primary/30">
                        {amenity}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Locked Content Placeholder */}
            {!hasAccess && (
              <Card className="glass-card border-white/10 border-dashed">
                <CardContent className="p-8 text-center">
                  <Lock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-display text-xl mb-2">Full Property Intelligence Locked</h3>
                  <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                    Unlock to access detailed description, amenities, walkthrough video, AI analysis, and personalized scoring.
                  </p>
                  <Button 
                    onClick={handleSelectProperty}
                    className="bg-primary text-primary-foreground"
                    data-testid="unlock-intelligence-btn"
                  >
                    <Unlock className="w-4 h-4 mr-2" /> Unlock Full Intelligence
                  </Button>
                </CardContent>
              </Card>
            )}
            
            {/* AI Analysis - Only if unlocked */}
            {hasAccess && (
              <>
                {/* Market Intelligence Report */}
                {marketIntelLoading ? (
                  <MarketIntelligenceLoader />
                ) : marketIntelError ? (
                  <MarketIntelligenceError onRetry={handleRetryMarketIntel} />
                ) : marketIntelligence ? (
                  <MarketIntelligenceReport 
                    marketIntelligence={marketIntelligence} 
                    propertyTitle={property.title}
                    propertyId={id}
                  />
                ) : null}
                
                {/* AI Analysis Card */}
                {aiLoading ? (
                  <Card className="glass-card border-white/10">
                    <CardContent className="p-8 text-center">
                      <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
                      <p className="text-sm text-muted-foreground">Generating AI Analysis...</p>
                    </CardContent>
                  </Card>
                ) : aiAnalysis ? (
                  <AIAnalysisCard analysis={aiAnalysis} />
                ) : null}
              </>
            )}
          </div>
          
          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-6">
            {/* Price Card */}
            <Card className="glass-card border-white/10 gold-glow sticky top-24">
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">Price</p>
                  <p className="text-4xl font-display text-primary mt-2">
                    {formatPrice(property.price, property.listing_type)}
                  </p>
                </div>
                
                <Separator className="my-6" />
                
                {/* Personalized Score - Only if unlocked */}
                {hasAccess && personalizedScore ? (
                  <PersonalizedScoreCard baseScore={baseScore} personalizedScore={personalizedScore} />
                ) : (
                  /* Basic Score for public view */
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs uppercase tracking-widest text-muted-foreground">Rashii Score</span>
                      <div className="flex items-center gap-1">
                        <Star className="w-5 h-5 text-primary fill-primary" />
                        <span className="text-2xl font-display text-primary">{publicProperty.rashii_score_headline}</span>
                        <span className="text-sm text-muted-foreground">/100</span>
                      </div>
                    </div>
                    <Progress value={publicProperty.rashii_score_headline} className="h-2 mb-4" />
                    
                    {!hasAccess && (
                      <p className="text-xs text-center text-muted-foreground border border-dashed border-white/20 rounded p-2">
                        <Lock className="w-3 h-3 inline mr-1" />
                        Unlock for personalized scoring based on your profile
                      </p>
                    )}
                  </div>
                )}
                
                <Separator className="my-6" />
                
                {/* Actions */}
                <div className="space-y-3">
                  {hasAccess ? (
                    <>
                      <Button 
                        className="w-full bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-bold h-12"
                        onClick={handleShortlist}
                        data-testid="shortlist-btn"
                      >
                        <Heart className="w-4 h-4 mr-2" /> Add to Shortlist
                      </Button>
                      
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button 
                            variant="outline" 
                            className="w-full border-primary/50 hover:bg-primary hover:text-primary-foreground uppercase tracking-widest text-xs h-12"
                            data-testid="make-offer-btn"
                          >
                            <AEDIcon className="w-4 h-4 mr-2" /> Make an Offer
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="glass-card border-white/10">
                          <DialogHeader>
                            <DialogTitle className="font-display text-xl">Make an Offer</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 pt-4">
                            <div className="space-y-2">
                              <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                                Your Offer (AED)
                              </Label>
                              <Input
                                type="number"
                                value={offerAmount}
                                onChange={(e) => setOfferAmount(e.target.value)}
                                className="bg-transparent border-white/20 focus:border-primary h-12"
                                data-testid="offer-amount-input"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                                Message (Optional)
                              </Label>
                              <Textarea
                                value={offerMessage}
                                onChange={(e) => setOfferMessage(e.target.value)}
                                placeholder="Add a note to your offer..."
                                className="bg-transparent border-white/20 focus:border-primary"
                                data-testid="offer-message-input"
                              />
                            </div>
                            <Button 
                              className="w-full bg-primary text-primary-foreground"
                              onClick={handleMakeOffer}
                              disabled={submitting}
                              data-testid="submit-offer-btn"
                            >
                              {submitting ? 'Submitting...' : 'Submit Offer'}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                      
                      <Button variant="outline" className="w-full text-xs h-12">
                        <Calendar className="w-4 h-4 mr-2" /> Schedule Viewing
                      </Button>
                    </>
                  ) : (
                    <Button 
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-bold h-12"
                      onClick={handleSelectProperty}
                      data-testid="select-unlock-btn"
                    >
                      <Unlock className="w-4 h-4 mr-2" /> Select to Unlock
                    </Button>
                  )}
                </div>
                
                {/* Trust Badges */}
                <div className="mt-6 pt-6 border-t border-border">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Shield className="w-4 h-4 text-primary" />
                    <span>Verified listing • Title deed confirmed</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Confidence Meter - Only if unlocked */}
            {hasAccess && confidenceMeter && (
              <RashiiConfidenceMeter confidenceMeter={confidenceMeter} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetailPage;
